import React from "react";
import LegalShell, { LegalSection } from "./LegalShell";
import { LEGAL } from "../../lib/legalConfig";

/**
 * /legal/privacy — Privacy Policy.
 *
 * ⚠️ LAWYER-APPROVED COPY (5 February 2026). Do NOT edit the prose without
 * going back to counsel.
 */
export default function PrivacyPage() {
  return (
    <LegalShell
      title="Privacy Policy"
      effectiveDate={LEGAL.PRIVACY_EFFECTIVE_DATE}
      lastUpdated={LEGAL.PRIVACY_EFFECTIVE_DATE}
      testid="legal-privacy"
      intro={
        <>
          We take care of your personal information — what we collect, how
          we use it, who we share it with, and how long we keep it.
        </>
      }
    >
      <LegalSection id="responsible" n="1" title="Who is responsible for your data">
        <p>
          Xchangely ("we", "us") is responsible for personal information we
          handle. Privacy contact: support@xchangely.io. We handle personal
          information in accordance with applicable data-protection laws,
          including Canada's Personal Information Protection and Electronic
          Documents Act (PIPEDA) and, where it applies to customers in the
          EU/EEA, the EU General Data Protection Regulation (GDPR).
        </p>
      </LegalSection>

      <LegalSection id="collect" n="2" title="Information we collect">
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Identity and verification data:</strong> name, date of
            birth, residential address, nationality, government-issued
            identity document, proof of address, and the results of identity
            and database checks.
          </li>
          <li>
            <strong>Contact data:</strong> email address and any contact
            details you provide.
          </li>
          <li>
            <strong>Transaction data:</strong> your orders, amounts, the
            crypto-asset and network, your destination wallet address, and
            payment confirmation details.
          </li>
          <li>
            <strong>Source-of-funds information</strong> where we are
            required to request it.
          </li>
          <li>
            <strong>Technical data:</strong> device, log and usage data, and
            cookies (see our Cookie Notice).
          </li>
        </ul>
        <p>
          We collect this information from you directly, from your
          identity-verification process, and from our payment partner in the
          course of processing your payment.
        </p>
      </LegalSection>

      <LegalSection id="why" n="3" title="Why we use it (and our lawful basis)">
        <p>
          We use personal information to: verify your identity and onboard
          you; provide and deliver the service; meet our legal obligations
          (anti-money-laundering, know-your-customer,
          sanctions screening, record-keeping and reporting); detect and
          prevent fraud and financial crime; provide support; and improve
          and secure the service. Our lawful bases include performance of
          our contract with you, compliance with legal obligations, and our
          legitimate interests in operating a secure and compliant service.
          Some information is required by law — if you do not provide it, we
          cannot serve you.
        </p>
      </LegalSection>

      <LegalSection id="share" n="4" title="Who we share it with">
        <p>
          We share personal information only as needed to run the service
          and meet our obligations, including with: our regulated{" "}
          <strong>payment partner</strong> (which performs identity
          verification, issues your deposit details and processes your bank
          payment); our <strong>identity-verification provider</strong>; and{" "}
          <strong>regulators and authorities</strong> where
          required or permitted by law. We do not sell your personal
          information. We do not display advertising.
        </p>
      </LegalSection>

      <LegalSection id="international" n="5" title="International processing">
        <p>
          Some processing — in particular
          payment-card and payment processing by our payment partner — may
          take place in other countries, including in the European Union. Where
          information is processed outside your country, we take steps to
          ensure a comparable level of protection by contract, and your
          information may be accessible to authorities in those
          jurisdictions in accordance with their laws.
        </p>
      </LegalSection>

      <LegalSection id="retention" n="6" title="How long we keep it">
        <p>
          We keep records and personal information for at least the period
          required by law (records connected to our regulatory obligations are
          retained for a minimum of five years from the relevant date),
          after which we securely delete or destroy them unless a longer
          retention period or legal hold applies.
        </p>
      </LegalSection>

      <LegalSection id="protect" n="7" title="How we protect it">
        <p>
          We apply administrative, technical and physical safeguards
          proportionate to sensitivity, including access on a least-privilege
          basis, multi-factor authentication, encryption of sensitive data
          in transit and at rest where practicable, and access logging. No
          system is perfectly secure, but we work to protect your
          information and to respond to incidents as the law requires.
        </p>
      </LegalSection>

      <LegalSection id="rights" n="8" title="Your rights">
        <p>
          Subject to law, you may request access to, or correction of, your
          personal information, and (where applicable, including under GDPR)
          request erasure, restriction, portability, or object to certain
          processing, and lodge a complaint with a supervisory authority.
          Some rights are limited where we must retain information by law —
          and we are prohibited by law from confirming or denying the
          existence or content of a suspicious-transaction report. To
          exercise your rights, contact support@xchangely.io.
        </p>
      </LegalSection>

      <LegalSection id="cookies" n="9" title="Cookies">
        <p>
          See our Cookie Notice for how we use cookies and similar
          technologies.
        </p>
      </LegalSection>

      <LegalSection id="changes" n="10" title="Changes">
        <p>
          We may update this policy; material changes will be notified
          through the site or by email.
        </p>
      </LegalSection>
    </LegalShell>
  );
}
